import { useState } from "react";
import Main from "../../_app/layouts/Main";
import ManageStatementTabBar from "../components/ManageStatementTabBar";
import { permissionCodes } from "../../permission/hooks";
import { CloudDownload as CloudDownloadIcon, DateRange as DateRangeIcon } from "../../_app/components/icons";
import { getInvoiceFile } from "../api";
import { downloadFile } from "../../_app/utils";
import { useFeedbackAlerts } from "../../_app/hooks";
import { useHasAnyFeatureEnabled } from "../../feature/hooks";
import { getInvoicesLabel } from "../utils";
import { createStylesheet } from "../../_app/utils/styles";
import {
  AdapterDateFns,
  DatePicker,
  DatePickerProps,
  LocalizationProvider,
  UIButton,
  UIInputAdornment,
  UITextField,
} from "../../_app/components";
import { Feature } from "../../feature/types";
import { useContextProvider } from "../../context/providers/ContextProvider";

export const Invoices = () => {
  const classes = useStyles();
  const selectedTab = "invoices";
  const [fromDate, setFromDate] = useState<Date | null>(new Date());
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [isDownloading, setIsDownloading] = useState(false);
  const hasStatementsFeature = useHasAnyFeatureEnabled(Feature.Statements);
  const { contextId: accountId } = useContextProvider();
  const { setFeedbackAlertError } = useFeedbackAlerts();

  const handleDownload = async () => {
    if ((!fromDate && !toDate) || isDownloading) return;
    setIsDownloading(true);
    try {
      const data = await getInvoiceFile(accountId, { fromDate, toDate });
      downloadFile(data, "invoice", "pdf");
    } catch {
      setFeedbackAlertError("We couldn't download the invoice. Please try again later.");
    } finally {
      setIsDownloading(false);
    }
  };

  const sharedProps: DatePickerProps<any, any> = {
    renderInput: (props: any) => <UITextField {...props} />,
    value: null,
    onChange: () => null,
    inputFormat: "dd/MM/yyyy",
    autoFocus: true,
    className: classes.input,
    InputProps: {
      endAdornment: (
        <UIInputAdornment position="end">
          <DateRangeIcon />
        </UIInputAdornment>
      ),
    },
  };

  return (
    <Main
      title={getInvoicesLabel(true, hasStatementsFeature)}
      data-cy="invoices-page"
      feature={Feature.Invoices}
      accessPermission={permissionCodes.BILLING_STATEMENT}
      needSelectedAccount={true}
    >
      <ManageStatementTabBar selectedTab={selectedTab}>
        <div className={classes.ctr}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              {...sharedProps}
              label="From Date"
              value={fromDate}
              onChange={(date) => setFromDate(date)}
              maxDate={toDate}
            />
            <DatePicker {...sharedProps} label="To Date" value={toDate} onChange={(date) => setToDate(date)} minDate={fromDate} />
          </LocalizationProvider>
          <div className={classes.break}>
            <UIButton
              variant="contained"
              color="primary"
              startIcon={!isDownloading ? <CloudDownloadIcon /> : undefined}
              isLoading={isDownloading}
              disabled={isDownloading}
              onClick={handleDownload}
            >
              Download
            </UIButton>
          </div>
        </div>
      </ManageStatementTabBar>
    </Main>
  );
};

const useStyles = createStylesheet((theme) => ({
  ctr: {
    display: "flex",
    gap: "20px",
    padding: theme.spacing(1),
    flexWrap: "wrap",
  },
  input: {
    minWidth: "125px",
    maxWidth: "200px",
    marginBottom: theme.spacing(1),
  },
  break: {
    flexBasis: "100%",
    height: "0",
  },
}));

export default Invoices;
